import { Button } from '@medly-components/core';
import { MarkdownContent } from '@medly-components/markdown';
import { NEXT, THANKS } from 'constants/text/common';
import { useSideNavContext } from 'context/SideNavContext/SideNavContext';
import { useBoolean, useIsMobile } from 'hooks';
import { useEffect, useRef, useState, type FC } from 'react';
import { CloseButton, DialogBox, DialogBoxContent, GuidedTourButton, Overlay, TourHeader, TourStepper } from './GuidedTour.styled';
import TourConfirmationDialog from './TourConfirmationDialog';
import { setDialogPosition } from './getDialogPosition';
import { GuidedTourProps } from './types';

export const GuidedTour: FC<GuidedTourProps> = ({
    showTour,
    steps,
    containerRef,
    showStepsCounter,
    onTourStart,
    onTourClose,
    tourStatusKey,
    showConfirmationDialog,
    confirmationText,
    resumeText,
    isWithoutAction
}) => {
    const isMobile = useIsMobile();
    const { isExpanded: isSideNavExpanded } = useSideNavContext();
    const dialogRef = useRef<HTMLDivElement>(null);
    const [stepIndex, setStepIndex] = useState(0);
    const isLastIndex = stepIndex === steps.length - 1;
    const { value: shouldFadeOut, setTrue: fadeOutConfirmationDialog, setFalse: resetFadeOut } = useBoolean(false);
    const { value: isConfirmationVisible, setTrue: showConfirmation, setFalse: hideConfirmation } = useBoolean(showConfirmationDialog);

    const handleDialogPosition = () => {
        const { targetRef, position, distance, targetScroll } = steps[stepIndex];
        if (dialogRef.current && targetRef.current && containerRef.current) {
            // Highlight the target element
            targetRef.current.style.zIndex = '2';
            targetRef.current.style.backgroundColor = 'white';
            targetScroll?.();
            setDialogPosition({
                targetRef,
                position,
                distance,
                containerRef,
                elementRef: dialogRef
            });
        }
    };

    useEffect(() => {
        showTour && hideConfirmation();
    }, [showTour]);

    useEffect(() => {
        showTour && handleDialogPosition();
    }, [showTour, stepIndex]);

    useEffect(() => {
        window.addEventListener('resize', handleDialogPosition);
        return () => {
            window.removeEventListener('resize', handleDialogPosition);
        };
    }, []);

    const resetCurrentStepStyles = () => {
        const targetRef = steps[stepIndex]?.targetRef;
        if (targetRef.current) {
            // Un highligh the target element
            targetRef.current.style.zIndex = '1';
            targetRef.current.style.backgroundColor = 'inherit';
        }
    };

    const handleTourContinueClick = () => {
        onTourStart();
        hideConfirmation();
    };

    const onCloseTourClick = () => {
        resetCurrentStepStyles();
        onTourClose();
    };

    const { position, content, header, onNext, primaryBtnLabel } = steps[stepIndex];

    const handleNext = () => {
        resetCurrentStepStyles();
        if (onNext) {
            onNext();
            return;
        }

        if (isLastIndex) {
            onTourClose();
            setStepIndex(0);
        } else {
            setStepIndex(stepIndex + 1);
        }
    };

    useEffect(() => {
        if (isConfirmationVisible) {
            setTimeout(() => {
                fadeOutConfirmationDialog();
                setTimeout(() => {
                    resetFadeOut();
                    hideConfirmation();
                }, 300);
            }, 5000);
        }
    }, [isConfirmationVisible]);

    if (!isSideNavExpanded) {
        return null;
    }

    const stepCounter = `Step ${stepIndex + 1} of ${steps.length}`;
    return isMobile ? null : (
        <>
            <GuidedTourButton data-testid="guided-tour-button" onClick={showConfirmation} />
            {isConfirmationVisible && (
                <TourConfirmationDialog
                    onCancel={hideConfirmation}
                    onContinue={handleTourContinueClick}
                    tourStatusKey={tourStatusKey}
                    confirmationText={confirmationText}
                    resumeText={resumeText}
                    fadeOut={shouldFadeOut}
                />
            )}
            {showTour ? (
                <>
                    <Overlay style={{ height: containerRef.current?.scrollHeight ? `${containerRef.current.scrollHeight}px` : '100%' }} />
                    <DialogBox data-testid="guided-tour-dialog" ref={dialogRef} position={position}>
                        <DialogBoxContent>
                            <CloseButton data-testid="guided-tour-close-button" onClick={onCloseTourClick} />
                            {showStepsCounter ? <TourStepper>{stepCounter}</TourStepper> : null}
                            {header ? <TourHeader>{header}</TourHeader> : null}
                            <MarkdownContent content={content} />
                        </DialogBoxContent>
                        {!isWithoutAction ? (
                            <Button size="S" onClick={handleNext}>
                                {primaryBtnLabel ? primaryBtnLabel : stepIndex === steps.length - 1 ? THANKS : NEXT}
                            </Button>
                        ) : null}
                    </DialogBox>
                </>
            ) : null}
        </>
    );
};

GuidedTour.displayName = 'GuidedTour';
